import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../../Components/Footer/Footer";
import siteConfig from "../../config/site";

const API_URL = siteConfig.apiUrl;

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("profile"); // profile | bookings | personas
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // Editable fields
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");

  // Bookings
  const [bookings, setBookings] = useState([]);

  // Persona editing
  const [editingPersona, setEditingPersona] = useState(null);
  const [personaForm, setPersonaForm] = useState({ tagline: "", bio: "", style: "" });

  // Photos
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (user && tab === "bookings") fetchBookings();
    if (user && tab === "photos") fetchPhotos();
  }, [tab, user]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, { credentials: "include" });
      if (res.status === 401) { navigate("/login"); return; }
      const data = await res.json();
      if (data.role === "admin") { navigate("/admin"); return; }
      setUser(data);
      setPhone(data.phone || "");
      setName(data.name || "");
      setLoading(false);
    } catch {
      setError("Failed to load profile.");
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const res = await fetch(`${API_URL}/entertainers/my-bookings`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setBookings(data.bookings || []);
      }
    } catch (e) {
      console.error("Failed to fetch bookings:", e);
    }
  };

  const fetchPhotos = async () => {
    try {
      const res = await fetch(`${API_URL}/media?type=image`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPhotos(data.media || []);
      }
    } catch (e) {
      console.error("Failed to fetch photos:", e);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingPhoto(true);
    setError("");

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", "profile");

      try {
        const res = await fetch(`${API_URL}/media/upload`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || `Failed to upload ${file.name}`);
        }
      } catch {
        setError(`Failed to upload ${file.name}`);
      }
    }

    setUploadingPhoto(false);
    fetchPhotos();
    e.target.value = "";
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const res = await fetch(`${API_URL}/entertainers/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ phone, name }),
      });
      if (!res.ok) throw new Error("Failed to save");
      const data = await res.json();
      setUser(prev => ({ ...prev, ...data.user }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditPersona = (persona) => {
    setEditingPersona(persona._id);
    setPersonaForm({
      tagline: persona.tagline || "",
      bio: persona.bio || "",
      style: persona.style || "",
    });
  };

  const handleSavePersona = async (personaId) => {
    try {
      const res = await fetch(`${API_URL}/entertainers/personas/${personaId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(personaForm),
      });
      if (!res.ok) throw new Error("Failed to save persona");
      await fetchProfile();
      setEditingPersona(null);
    } catch {
      setError("Failed to save persona.");
    }
  };

  const handleLogout = async () => {
    await fetch(`${API_URL}/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="bg-[#0a0a0f] min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-[#0a0a0f] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 font-body mb-4">Please sign in to view your profile.</p>
          <Link to="/login" className="text-purple-400 hover:text-purple-300 font-body">Sign in →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0f] min-h-screen">
      <div className="h-20" />

      <section className="py-8 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              {user.avatar && (
                <img src={user.avatar} alt={user.name}
                  className="w-14 h-14 rounded-full border-2 border-purple-700/50" />
              )}
              <div>
                <h1 className="font-heading text-2xl font-bold text-white">{user.name}</h1>
                <p className="text-gray-500 text-sm font-body">{user.email}</p>
              </div>
            </div>
            <button onClick={handleLogout}
              className="text-gray-500 hover:text-red-400 text-sm font-body transition-colors">
              Sign Out
            </button>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-6 p-3 rounded-lg bg-red-900/30 border border-red-800/50 text-red-400 text-sm font-body flex justify-between">
              <span>{error}</span>
              <button onClick={() => setError("")} className="text-red-600 hover:text-red-400">✕</button>
            </div>
          )}
          {saved && (
            <div className="mb-6 p-3 rounded-lg bg-green-900/30 border border-green-800/50 text-green-400 text-sm font-body">
              ✓ Profile saved
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-gray-800 mb-6">
            {[
              { id: "profile", label: "My Profile", icon: "👤" },
              { id: "personas", label: "My Personas", icon: "🎭" },
              { id: "photos", label: "My Photos", icon: "📷" },
              { id: "bookings", label: "My Bookings", icon: "📅" },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-4 sm:px-6 py-3 text-sm font-body font-medium transition-colors ${
                  tab === t.id
                    ? "text-white border-b-2 border-purple-500"
                    : "text-gray-500 hover:text-gray-300"
                }`}>
                <span className="mr-2">{t.icon}</span>{t.label}
              </button>
            ))}
          </div>

          {/* ── Profile Tab ── */}
          {tab === "profile" && (
            <form onSubmit={handleSaveProfile} className="bg-gray-900/30 border border-gray-800/50 rounded-xl p-6">
              <h2 className="font-heading text-lg text-white mb-6">Profile Settings</h2>
              <div className="space-y-5">
                <div>
                  <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2 font-body">
                    Display Name
                  </label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-gray-900/60 border border-gray-700/50 text-white placeholder-gray-600 focus:border-purple-600 focus:outline-none focus:ring-1 focus:ring-purple-600 transition-all font-body"
                    placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2 font-body">
                    Phone Number
                  </label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-gray-900/60 border border-gray-700/50 text-white placeholder-gray-600 focus:border-purple-600 focus:outline-none focus:ring-1 focus:ring-purple-600 transition-all font-body"
                    placeholder="+1 (403) 555-0123" />
                  <p className="text-gray-600 text-xs mt-2 font-body">
                    Used for booking notifications and schedule reminders.
                  </p>
                </div>
              </div>
              <button type="submit" disabled={saving}
                className="mt-6 px-6 py-2.5 bg-gold text-black font-semibold text-sm uppercase tracking-wider rounded-lg hover:bg-gold-light transition-all duration-300 disabled:opacity-50 font-body">
                {saving ? "Saving..." : "Save Profile"}
              </button>
            </form>
          )}

          {/* ── Personas Tab ── */}
          {tab === "personas" && (
            <div>
              {(!user.personas || user.personas.length === 0) ? (
                <div className="text-center py-16">
                  <div className="text-4xl mb-4">🎭</div>
                  <p className="text-gray-500 font-body">No personas assigned yet.</p>
                  <p className="text-gray-600 text-sm font-body mt-2">Contact the admin to set up your personas.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {user.personas.map(persona => (
                    <div key={persona._id} className="bg-gray-900/40 border border-gray-800/50 rounded-xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-heading text-xl text-white">{persona.name}</h3>
                          {persona.tagline && !editingPersona && (
                            <p className="text-gray-400 text-sm font-body">{persona.tagline}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full ${persona.active ? "bg-green-500" : "bg-gray-600"}`} />
                          {editingPersona !== persona._id ? (
                            <button onClick={() => handleEditPersona(persona)}
                              className="text-purple-400 hover:text-purple-300 text-sm font-body transition-colors">
                              Edit
                            </button>
                          ) : (
                            <div className="flex gap-2">
                              <button onClick={() => handleSavePersona(persona._id)}
                                className="text-green-400 hover:text-green-300 text-sm font-body transition-colors">
                                Save
                              </button>
                              <button onClick={() => setEditingPersona(null)}
                                className="text-gray-500 hover:text-gray-400 text-sm font-body transition-colors">
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {editingPersona === persona._id ? (
                        <div className="space-y-4 mt-4">
                          <div>
                            <label className="block text-gray-400 text-xs uppercase tracking-wider mb-1 font-body">Tagline</label>
                            <input type="text" value={personaForm.tagline}
                              onChange={(e) => setPersonaForm(p => ({ ...p, tagline: e.target.value }))}
                              className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700/50 text-white text-sm focus:border-purple-600 focus:outline-none font-body"
                              placeholder="e.g. Playful · High-Energy · Party Vibe" />
                          </div>
                          <div>
                            <label className="block text-gray-400 text-xs uppercase tracking-wider mb-1 font-body">Bio</label>
                            <textarea value={personaForm.bio}
                              onChange={(e) => setPersonaForm(p => ({ ...p, bio: e.target.value }))}
                              rows={3}
                              className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700/50 text-white text-sm focus:border-purple-600 focus:outline-none resize-none font-body"
                              placeholder="Describe this persona..." />
                          </div>
                          <div>
                            <label className="block text-gray-400 text-xs uppercase tracking-wider mb-1 font-body">Style</label>
                            <input type="text" value={personaForm.style}
                              onChange={(e) => setPersonaForm(p => ({ ...p, style: e.target.value }))}
                              className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700/50 text-white text-sm focus:border-purple-600 focus:outline-none font-body"
                              placeholder="e.g. High-energy dance, playful interaction" />
                          </div>
                        </div>
                      ) : (
                        <>
                          {persona.bio && <p className="text-gray-400 text-sm font-body mt-2">{persona.bio}</p>}
                          {persona.style && (
                            <p className="text-gray-600 text-xs font-body mt-2">
                              <span className="text-gray-500">Style:</span> {persona.style}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Photos Tab ── */}
          {tab === "photos" && (
            <div>
              <div className="mb-6">
                <label className="px-5 py-2.5 bg-purple-700 hover:bg-purple-600 text-white font-body text-sm font-semibold rounded-lg transition-colors cursor-pointer">
                  {uploadingPhoto ? "Uploading..." : "📷 Upload Photos"}
                  <input type="file" accept="image/*" multiple onChange={handlePhotoUpload}
                    className="hidden" disabled={uploadingPhoto} />
                </label>
                <p className="text-gray-600 text-xs font-body mt-2">
                  Photos will be reviewed by admin before they appear on the site.
                </p>
              </div>

              {photos.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">📷</div>
                  <p className="text-gray-500 font-body">No photos uploaded yet.</p>
                  <p className="text-gray-600 text-sm font-body mt-2">
                    Upload photos for your profile — they'll be reviewed by admin.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {photos.map(photo => (
                    <div key={photo._id} className="bg-gray-900/40 border border-gray-800/50 rounded-xl overflow-hidden">
                      <img src={photo.url} alt={photo.originalName} className="w-full h-40 object-cover" />
                      <div className="p-2 flex items-center justify-between">
                        <span className="text-gray-400 text-xs font-body truncate">{photo.originalName}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-body ${
                          photo.status === "approved" ? "bg-green-900/40 text-green-400" :
                          photo.status === "rejected" ? "bg-red-900/40 text-red-400" :
                          "bg-yellow-900/40 text-yellow-400"
                        }`}>
                          {photo.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Bookings Tab ── */}
          {tab === "bookings" && (
            <div>
              {bookings.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-4xl mb-4">📅</div>
                  <p className="text-gray-500 font-body">No bookings assigned to you yet.</p>
                  <p className="text-gray-600 text-sm font-body mt-2">
                    When the admin assigns a booking to one of your personas, it'll show up here.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bookings.map(b => (
                    <div key={b._id} className="bg-gray-900/40 border border-gray-800/50 rounded-xl p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-white font-heading text-lg">{b.clientName}</h3>
                          <p className="text-gray-500 text-sm font-body">{b.serviceType || "Private Show"}</p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-body uppercase tracking-wider ${
                          b.status === "confirmed" ? "bg-green-900/40 text-green-400 border border-green-800/40" :
                          b.status === "inquiry" ? "bg-yellow-900/40 text-yellow-400 border border-yellow-800/40" :
                          b.status === "cancelled" ? "bg-red-900/40 text-red-400 border border-red-800/40" :
                          "bg-gray-800 text-gray-400 border border-gray-700"
                        }`}>
                          {b.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm font-body text-gray-400">
                        <span>📅 {new Date(b.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
                        {b.startTime && <span>🕐 {b.startTime}</span>}
                        <span>🎭 {b.personaId?.name || "—"}</span>
                        {b.location && <span>📍 {b.location}</span>}
                        {b.duration && <span>⏱ {b.duration} min</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Footer link */}
          <div className="mt-8 pt-4 border-t border-gray-800/50">
            <Link to="/" className="text-gray-600 hover:text-gray-400 text-sm font-body transition-colors">
              ← Back to site
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Profile;
